import { useEffect, useRef, useState } from "react";
import { Check, Film, ImageIcon, Loader2, Sparkles, Trash2, Upload, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiDelete, apiGet, apiPost } from "@/lib/apiAdmin";
import { HUD } from "./theme";
import { Switch } from "@/components/ui/switch";
import { VIDEO_PRESETS } from "@/lib/heroLayout";
import { pickCar, useWashCars } from "@/lib/washOptions";

const pill = (on) => `transition-colors border ${on ? "border-cyan-300 bg-cyan-400/15 text-white" : "border-cyan-400/15 text-gray-400 hover:text-white"}`;
const MODES = [
  ["image", "My photo", "A still picture behind your headline"],
  ["video", "My video", "A silent clip behind your headline (plays once or loops)"],
  ["car", "Still car (no wash)", "Your homepage car, sharp and still"],
  ["plain", "Plain dark", "Just the headline and buttons"],
];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Thumb = ({ m, on, pick, remove }) => (
  <div className={`relative rounded-xl overflow-hidden ${pill(on)}`} data-testid={`hero-media-${m.id}`}>
    <button onClick={pick} className="block w-full aspect-video bg-black" data-testid={`hero-media-pick-${m.id}`}>
      {m.kind === "video"
        ? <video src={`${BACKEND}${m.url}`} muted loop playsInline autoPlay className="w-full h-full object-cover" />
        : <img src={`${BACKEND}${m.url}`} alt="" className="w-full h-full object-cover" />}
    </button>
    <span className="absolute top-1.5 left-1.5 text-[10px] bg-black/70 text-cyan-200 px-1.5 rounded flex items-center gap-1">
      {m.kind === "video" ? <Film className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}{m.source === "ai" ? "AI" : "yours"}
    </span>
    {on && <span className="absolute top-1.5 right-9 w-6 h-6 rounded-full bg-cyan-400 text-black flex items-center justify-center"><Check className="w-3.5 h-3.5" /></span>}
    <button onClick={remove} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center text-gray-300 hover:text-rose-300" data-testid={`hero-media-delete-${m.id}`}>
      <Trash2 className="w-3 h-3" />
    </button>
  </div>
);

const UploadBtn = ({ onDone }) => {
  const ref = useRef(null);
  const [pct, setPct] = useState(null);
  const send = (file) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) return toast.error("File too large (max 60 MB)");
    const fd = new FormData();
    fd.append("file", file);
    setPct(0);
    api.post("/admin/hero/upload", fd, { onUploadProgress: (e) => setPct(Math.round((100 * e.loaded) / (e.total || 1))), timeout: 0 })
      .then((r) => { toast.success(r.data.kind === "video" ? "Video saved (sound removed)" : "Photo saved"); onDone(r.data); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Upload failed"))
      .finally(() => setPct(null));
  };
  return (
    <button onClick={() => ref.current.click()} disabled={pct !== null} className={`${HUD.ghost} h-10 px-4 text-sm flex items-center gap-2`} data-testid="hero-upload-btn">
      {pct !== null ? <><Loader2 className="w-4 h-4 animate-spin" />{pct < 100 ? `Uploading ${pct}%` : "Removing sound & saving…"}</> : <><Upload className="w-4 h-4" />Upload photo or video</>}
      <input ref={ref} type="file" accept="image/*,video/*" hidden onChange={(e) => { send(e.target.files[0]); e.target.value = ""; }} data-testid="hero-upload-input" />
    </button>
  );
};

const AiGenerate = ({ onDone, carSrc, carPos }) => {
  const [kind, setKind] = useState("image");
  const [prompt, setPrompt] = useState("");
  const [preset, setPreset] = useState(null);
  const [useCar, setUseCar] = useState(true);
  const [job, setJob] = useState(null);
  useEffect(() => {
    if (!job || job.status !== "running") return undefined;
    const t = setInterval(() => apiGet(`/admin/hero/jobs/${job.job_id}`).then((j) => {
      if (j.status === "done") { toast.success(`AI ${j.kind === "video" ? "video" : "photo"} saved to your library`); onDone(j.media); }
      if (j.status === "failed") toast.error(j.error || "Generation failed");
      if (j.status !== "running") setJob(null);
    }).catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [job, onDone]);
  const go = () => apiPost("/admin/hero/generate", { kind, prompt, overlay: preset?.overlay || "", motion: preset?.motion || "", car_pos: carPos, ...(kind === "video" && useCar && carSrc ? { image_src: carSrc } : {}) }).then(setJob).catch((e) => toast.error(e?.response?.data?.detail || "Couldn't start"));
  const busy = job?.status === "running";
  return (
    <div className="rounded-xl border border-cyan-400/10 p-3 space-y-3" data-testid="hero-ai">
      <p className="text-sm text-white flex items-center gap-2"><Sparkles className="w-4 h-4 text-cyan-300" />Create with AI</p>
      <div className="flex gap-2">
        {[["image", "Photo"], ["video", "Video (5s)"]].map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)} disabled={busy} className={`h-8 px-4 rounded-full text-xs ${pill(kind === k)}`} data-testid={`hero-ai-kind-${k}`}>{l}</button>
        ))}
      </div>
      {kind === "video" && <>
        <div className="flex flex-wrap gap-1.5" data-testid="hero-ai-presets">
          {VIDEO_PRESETS.map((x) => (
            <button key={x.id} onClick={() => { setPreset(x); setPrompt(x.prompt); }} disabled={busy} data-testid={`hero-ai-preset-${x.id}`}
              className={`h-7 px-3 rounded-full text-[11px] ${pill(preset?.id === x.id)} ${preset?.id === x.id ? "ring-2 ring-cyan-300/60" : ""}`} aria-pressed={preset?.id === x.id}>{x.label}</button>
          ))}
        </div>
        {carSrc && <label className="flex items-center gap-2 text-xs text-gray-300"><Switch checked={useCar} onCheckedChange={setUseCar} data-testid="hero-ai-use-car" />Animate my current homepage car (same car, still camera)</label>}
        {preset?.motion === "drive" && <p className="text-[11px] text-cyan-200">The car drives in, parks where your photo shows it and stays there — the lettering rides in with it.</p>}
        {preset?.overlay === "ceramic" && <p className="text-[11px] text-cyan-200">Adds a "Ceramic Coated" label with an arrow pointing at the paint.</p>}
      </>}
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} disabled={busy} rows={2} data-testid="hero-ai-prompt"
        placeholder="e.g. Glossy black Tahoe with water beading under a desert sunset in Maricopa"
        className={`w-full rounded-xl px-3 py-2 text-sm border ${HUD.input}`} />
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={go} disabled={busy || prompt.trim().length < 3} className={`${HUD.btn} h-9 px-4 text-xs flex items-center gap-2 disabled:opacity-40`} data-testid="hero-ai-generate">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}{busy ? "Creating…" : "Generate"}
        </button>
        <p className="text-[11px] text-gray-500" data-testid="hero-ai-status">{busy ? (job.kind === "video" ? "Videos take 1–3 minutes — you can keep working." : "Photos take about 20 seconds.") : "Photos by Nano Banana, videos by Kling. Saved to your library automatically."}</p>
      </div>
    </div>
  );
};

export const HeroBackgroundSection = () => {
  const [v, setV] = useState(null);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const cars = useWashCars();
  const load = () => apiGet("/admin/hero/media").then((d) => setItems(d.items)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/site/content").then((r) => { const c = r.data.content || {}; setV({ mode: c["hero.mode"] === "animation" || !c["hero.mode"] ? "car" : c["hero.mode"], url: c["hero.bg_url"] || "", carId: c["wash.car"] || "rangerover", carPos: c["hero.car_pos"] || "" }); }).catch(() => setV({ mode: "car", url: "", carId: "rangerover", carPos: "" }));
    const onMode = (e) => setV((x) => (x ? { ...x, mode: e.detail } : x));
    window.addEventListener("hero-mode", onMode);
    return () => window.removeEventListener("hero-mode", onMode);
  }, []);
  const added = (m) => { load(); setV((x) => ({ ...x, url: m.url, mode: m.kind })); };
  const remove = (m) => window.confirm("Delete this from your library?") && apiDelete(`/admin/hero/media/${m.id}`).then(() => { load(); if (v.url === m.url) setV({ ...v, mode: "car", url: "" }); });
  const save = () => {
    const sel = items.find((m) => m.url === v.url);
    if ((v.mode === "image" || v.mode === "video") && (!sel || sel.kind !== v.mode)) return toast.error(`Pick a ${v.mode === "image" ? "photo" : "video"} from your library first`);
    setSaving(true);
    api.put("/admin/site/content", { "hero.mode": v.mode, ...(sel && (v.mode === "image" || v.mode === "video") ? { "hero.bg_url": sel.url, "hero.bg_kind": sel.kind, "hero.overlay": sel.overlay || "", "hero.bg_motion": sel.motion || "" } : {}) })
      .then(() => { toast.success("Homepage updated"); window.dispatchEvent(new CustomEvent("hero-mode", { detail: v.mode })); }).catch((e) => toast.error(e?.response?.data?.detail || "Save failed")).finally(() => setSaving(false));
  };
  if (!v) return <Loader2 className="w-6 h-6 animate-spin text-cyan-300" />;
  const shown = items.filter((m) => m.kind === v.mode);
  const lib = items.find((m) => m.url === v.url && m.kind === "image");
  const carSrc = (v.mode === "image" && lib?.url) || pickCar(v.carId, cars).clean;
  return (
    <div className={`${HUD.panel} p-5 space-y-5`} data-testid="hero-background-section">
      <div>
        <h3 className="text-white font-['Outfit'] text-lg">Homepage top section</h3>
        <p className="text-xs text-gray-400 mt-1">Show the still car, or use your own photo or video. Uploaded videos have their sound removed and play silently (choose loop or play-once under Lettering).</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {MODES.map(([k, l, d]) => (
          <button key={k} onClick={() => setV({ ...v, mode: k })} className={`rounded-xl p-3 text-left ${pill(v.mode === k)}`} data-testid={`hero-mode-${k}`}>
            <p className="text-sm">{l}</p><p className="text-[11px] text-gray-500">{d}</p>
          </button>
        ))}
      </div>
      {(v.mode === "image" || v.mode === "video") && <>
        <div className="flex flex-wrap gap-2"><UploadBtn onDone={added} /></div>
        <AiGenerate onDone={added} carSrc={carSrc} carPos={v.carPos} />
        <div>
          <p className={`${HUD.label} mb-2`}>Your library — tap one to use it</p>
          {shown.length === 0
            ? <p className="text-sm text-gray-500" data-testid="hero-library-empty">No {v.mode === "image" ? "photos" : "videos"} yet — upload one or create one with AI.</p>
            : <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{shown.map((m) => <Thumb key={m.id} m={m} on={v.url === m.url} pick={() => setV({ ...v, url: m.url })} remove={() => remove(m)} />)}</div>}
        </div>
      </>}
      <div className="flex flex-wrap gap-2">
        <button onClick={save} disabled={saving} className={`${HUD.btn} h-10 px-5 text-sm flex items-center gap-2`} data-testid="hero-save">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save homepage
        </button>
      </div>
    </div>
  );
};
